import { ComponentProps, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Rarity } from '__generated__/globalTypes';
import { ScarcityIcon } from 'atoms/icons/ScarcityIcon';
import { FilterAccordion } from 'atoms/layout/Accordion';
import {
  CollectionsFilterType,
  RarityCount,
  SeasonCount,
} from 'components/collections/filters/types';
import { FilterSection } from 'components/search/FilterSection';
import { FilterTitle } from 'components/search/FilterTitle';
import { Option } from 'components/search/Option';
import { cardAttributes } from 'lib/glossary';
import { scarcityMessages } from 'lib/scarcity';

type FilterOptionDefinition = Omit<ComponentProps<typeof Option>, 'variant'> & {
  value: string | number;
};

type FilterDefinition = {
  id: CollectionsFilterType;
  title: ReactNode;
  filters: FilterOptionDefinition[];
};

type Props = {
  rarities: Rarity[] | undefined;
  raritiesCount: RarityCount[];
  seasonStartYears: number[] | undefined;
  seasonsCount: SeasonCount[];
  hide?: CollectionsFilterType[];
  onRaritiesChange: (newRarities: Rarity[] | undefined) => void;
  onSeasonsChange: (newSeasons: number[] | undefined) => void;
};

export const CheckboxFilters = ({
  rarities,
  raritiesCount,
  seasonStartYears,
  seasonsCount,
  hide = [],
  onRaritiesChange,
  onSeasonsChange,
}: Props) => {
  const { formatMessage } = useIntl();

  const toggleItemInlist = <T,>(
    array: T[] | undefined,
    item: T,
    onChangeFunction: (newArray: T[] | undefined) => void
  ) => {
    if (array?.includes(item)) {
      const itemExcludedArray = array.filter(i => i !== item);
      onChangeFunction(
        itemExcludedArray.length ? itemExcludedArray : undefined
      );
    } else {
      onChangeFunction(array ? [...array, item] : [item]);
    }
  };

  const seasonsFilters = seasonsCount.map(({ season, count }) => ({
    value: season.startYear,
    label: season.name,
    count,
    active: !!seasonStartYears?.includes(season.startYear),
    onClick: () => {
      toggleItemInlist(seasonStartYears, season.startYear, onSeasonsChange);
    },
    before: null,
  }));

  const rarityFilters = raritiesCount.map(({ rarity, count }) => ({
    value: rarity,
    label: formatMessage(scarcityMessages[rarity]),
    count,
    active: !!rarities?.includes(rarity),
    onClick: () => {
      toggleItemInlist(rarities, rarity, onRaritiesChange);
    },
    before: <ScarcityIcon scarcity={rarity} size="md" />,
  }));

  const allFilters: FilterDefinition[] = [
    {
      id: CollectionsFilterType.rarities,
      title: <FilterTitle name={cardAttributes.scarcity} />,
      filters: rarityFilters,
    },
    {
      id: CollectionsFilterType.seasonStartYears,
      title: <FilterTitle name={cardAttributes.season} />,
      filters: seasonsFilters,
    },
  ];
  const shownFilters = allFilters.filter(({ id }) => !hide.includes(id));
  return (
    <>
      {shownFilters.map(({ title, filters, id }) => (
        <FilterAccordion startsOpen title={title} key={id}>
          <FilterSection>
            {filters.map(({ value, label, count, active, onClick, before }) => (
              <Option
                variant="checkbox"
                key={value}
                label={label}
                onClick={onClick}
                count={count}
                active={active}
                before={before}
              />
            ))}
          </FilterSection>
        </FilterAccordion>
      ))}
    </>
  );
};
